import {useEffect, useMemo, useState} from "react";
import {Box, Table, TableBody, TableCell, TableHead, TablePagination, TableRow} from "@mui/material";
import {PhoneNumberRow} from "./PhoneNumberRow";
import {PhoneNumbersToolbar} from "./PhoneNumbersToolbar";
import {headerCellSx} from "../../constants";
import {paginate} from "../../utils";

// `numbers` is the object returned by usePhoneNumbers()
export const PhoneNumbersTable = ({numbers}) => {
  const {
    phoneNumbers, updateNumber, deleteNumber, removeDuplicates, clear,
    isDuplicate, isEmpty, hasIssue, duplicateCount, emptyCount, invalidCount
  } = numbers;
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [onlyIssues, setOnlyIssues] = useState(false);

  // Nothing left to filter once every issue is fixed
  useEffect(() => {
    if (invalidCount === 0) setOnlyIssues(false);
  }, [invalidCount]);

  // Row number shown in the table (position in the full list)
  const indexById = useMemo(() => {
    const map = new Map();
    phoneNumbers.forEach((row, idx) => map.set(row.id, idx + 1));
    return map;
  }, [phoneNumbers]);

  const visibleNumbers = onlyIssues ? phoneNumbers.filter(hasIssue) : phoneNumbers;
  const {currentPage, pageItems} = paginate(visibleNumbers, page, rowsPerPage);

  return (
    <>
      <PhoneNumbersToolbar
        total={phoneNumbers.length}
        duplicateCount={duplicateCount}
        emptyCount={emptyCount}
        onlyIssues={onlyIssues}
        onOnlyIssuesChange={(checked) => {
          setOnlyIssues(checked);
          setPage(0);
        }}
        onRemoveDuplicates={removeDuplicates}
        onClear={clear}
      />

      {phoneNumbers.length > 0 &&
        <Box sx={{border: 1, borderColor: "divider", borderRadius: 2, overflow: "hidden"}}>
          <Box sx={{maxHeight: 320, overflowY: "auto"}}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{...headerCellSx, width: 60}}>#</TableCell>
                  <TableCell sx={headerCellSx}>Phone number</TableCell>
                  <TableCell padding="checkbox" sx={headerCellSx}/>
                </TableRow>
              </TableHead>
              <TableBody>
                {pageItems.map(row => (
                  <PhoneNumberRow
                    key={row.id}
                    row={row}
                    index={indexById.get(row.id)}
                    duplicate={isDuplicate(row)}
                    empty={isEmpty(row)}
                    onChange={updateNumber}
                    onDelete={deleteNumber}
                  />
                ))}
              </TableBody>
            </Table>
          </Box>
          <TablePagination
            component="div"
            count={visibleNumbers.length}
            page={currentPage}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[25, 50, 100]}
          />
        </Box>
      }
    </>
  )
}
